import {DestroyRef, DOCUMENT, Injectable, NgZone, inject, signal} from '@angular/core';

/** How often an open tab asks whether a newer build has been deployed (REN-6298). */
export const VERSION_CHECK_INTERVAL_MS = 5 * 60_000;
/** A tab coming back into view checks at once, but no more often than this. */
export const VERSION_CHECK_MIN_GAP_MS = 60_000;

/** `main-YCYI5SXO.js` (application builder), `main.2b0ed21e41c4873d.js` (browser builder), `main.js` unhashed. */
const MAIN_BUNDLE = /^main(?:[.-][A-Za-z0-9]+)?\.js$/;

/** The main bundle a document loads. Its hashed name changes with every build that changes code. */
export function mainBundleOf(doc: Pick<Document, 'querySelectorAll'>): string | null {
  for (const script of Array.from(doc.querySelectorAll('script[src]'))) {
    const name = (script.getAttribute('src') ?? '').split(/[?#]/, 1)[0].split('/').pop() ?? '';
    if (MAIN_BUNDLE.test(name)) return name;
  }
  return null;
}

/**
 * Notices that a newer build has been deployed while this tab is open (REN-6298), by fetching
 * index.html past every cache and comparing the main bundle it names with the one this tab runs.
 * Nothing is compared when the page names none, so a test bed never polls.
 */
@Injectable({providedIn: 'root'})
export class AppVersionService {
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  private readonly running = mainBundleOf(this.document);
  private readonly _updateAvailable = signal(false);
  readonly updateAvailable = this._updateAvailable.asReadonly();

  private started = false;
  private lastCheck = Date.now();
  private timer: ReturnType<typeof setInterval> | undefined;

  /** Starts the periodic check and the check on returning to the tab. Idempotent. */
  start(): void {
    if (this.started || this.running === null) return;
    this.started = true;
    const onVisible = (): void => {
      if (this.document.visibilityState === 'visible' && Date.now() - this.lastCheck >= VERSION_CHECK_MIN_GAP_MS) {
        void this.check();
      }
    };
    // Outside the zone: an in-zone interval would run change detection on every tick.
    this.zone.runOutsideAngular(() => {
      this.timer = setInterval(() => void this.check(), VERSION_CHECK_INTERVAL_MS);
      this.document.addEventListener('visibilitychange', onVisible);
      this.destroyRef.onDestroy(() => {
        clearInterval(this.timer);
        this.document.removeEventListener('visibilitychange', onVisible);
      });
    });
  }

  /** True when the deployed index.html names a different main bundle than this tab runs. */
  async check(): Promise<boolean> {
    if (this._updateAvailable() || this.running === null) return this._updateAvailable();
    this.lastCheck = Date.now();
    let deployed: string | null;
    try {
      const response = await fetch('index.html', {cache: 'no-store'});
      if (!response.ok) return false;
      deployed = mainBundleOf(new DOMParser().parseFromString(await response.text(), 'text/html'));
    } catch {
      return false;
    }
    if (deployed === null || deployed === this.running) return false;
    clearInterval(this.timer);
    this.zone.run(() => this._updateAvailable.set(true));
    return true;
  }

  reload(): void {
    this.document.location.reload();
  }
}
